import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    fetchAllUsers,
    deleteSingleUser,
    setUserFilters,
} from "../../slices/users/thunk";
import { Button } from "reactstrap";
import UserHistoryModal from "./UserHistoryModal";
import PasswordUpdateModal from "./PasswordUpdateModal";
import UserStatusUpdateModal from "./UserStatusUpdateModal";
import CreateUserModal from "./CreateUserModal";
import WinMultiplierModal from "./WinMultiplierModal";
import CommonDataTable from "../../common/DataTable";
import DeleteDialog from "../../Components/Common/DeleteDailog";
import { formatDateDDMMYYYY } from "../../helpers/dateUtils";
import { getOperatorDropdown } from "../../helpers/apis";
import { getLoggedinUser } from "../../helpers/api_helper";

// Settings Thunks
import { fetchSettingsData as fetchLT } from "../../slices/lt/settings/thunk";
import { fetchSettingsData as fetchLT1 } from "../../slices/lt1/settings/thunk";
import { fetchSettingsData as fetchRO } from "../../slices/ro/settings/thunk";

import { socket } from "../../common/socket";
import { UPDATE_USER_ONLINE_STATUS } from "../../slices/users/thunk";

const Users = ({ gameType }) => {
    const dispatch = useDispatch();

    // Get current logged-in user
    const currentUser = getLoggedinUser();
    const userProfile = currentUser?.user || currentUser;

    // Check if current user is an operator
    const isCurrentUserOperator = userProfile?.userType === 'operator' ||
        userProfile?.role === 'operator' ||
        userProfile?.isOperator === true;

    // Get state from Redux store
    const {
        users,
        loading,
        deleteLoading,
        bulkDeleteLoading,
        historyLoading,
        pagination,
        filters,
        error
    } = useSelector((state) => state.Users);

    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showMultiplierModal, setShowMultiplierModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [operators, setOperators] = useState([]);
    const [loadingOperators, setLoadingOperators] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [roleFilter, setRoleFilter] = useState("");
    const isMounted = useRef(true);
    const initialFetchDone = useRef(false);

    // Global settings selectors
    const ltGlobal = useSelector(state => state.lt?.settings?.globalReturnMultiplier);
    const lt1Global = useSelector(state => state.lt1?.settings?.globalReturnMultiplier);
    const roGlobal = useSelector(state => state.ro?.settings?.globalReturnMultiplier);

    const getGlobalMultiplier = () => {
        if (gameType === 'lt1') return lt1Global;
        if (gameType === 'ro') return roGlobal;
        return ltGlobal;
    };

    // Fix: Fetch users with stable dependency
    const fetchUsers = useCallback(() => {
        if (isMounted.current) {
            dispatch(fetchAllUsers({ ...filters, gameType }));
        }
    }, [dispatch, filters?.page, filters?.limit, filters?.search, filters?.status, filters?.operatorId, gameType]); // Add operatorId dependency

    // Fetch users only once on mount and when filters intentionally change
    useEffect(() => {
        if (isMounted.current) {
            const timer = setTimeout(() => {
                fetchUsers();
            }, 300); // Small delay to prevent rapid API calls

            return () => {
                clearTimeout(timer);
            };
        }
    }, [fetchUsers]);

    useEffect(() => {
        const fetchOperators = async () => {
            setLoadingOperators(true);
            try {
                const response = await getOperatorDropdown();
                if (response.success || response.status === 1) {
                    setOperators(response.data || []);
                }
            } catch (error) {
                console.error("Error fetching operators:", error);
            } finally {
                setLoadingOperators(false);
            }
        };
        fetchOperators();
    }, []);


    // Real-time socket listener for user online status
    useEffect(() => {
        if (!socket.connected) socket.connect();
        
        const handleStatusChange = (data) => {
            console.log("[Admin] User status changed:", data);
            dispatch({
                type: UPDATE_USER_ONLINE_STATUS,
                payload: {
                    userId: data.userId,
                    status: data.status,
                    isOnline: data.isOnline,
                    game: data.game
                }
            });
        };

        socket.on("admin_user_status_changed", handleStatusChange);

        return () => {
            socket.off("admin_user_status_changed", handleStatusChange);
        };
    }, [dispatch]);

    // Fetch Global Settings for inheritance display
    useEffect(() => {
        if (gameType === 'lt1') dispatch(fetchLT1());
        else if (gameType === 'ro') dispatch(fetchRO());
        else dispatch(fetchLT());
    }, [dispatch, gameType]);

    // Handle filter changes - debounced version
    const updateFilterParams = useCallback((newParams) => {
        // Prevent unnecessary updates
        const currentFiltersStr = JSON.stringify(filters);
        const newFiltersStr = JSON.stringify({ ...filters, ...newParams });

        if (currentFiltersStr !== newFiltersStr) {
            dispatch(setUserFilters(newParams));
        }
    }, [dispatch, JSON.stringify(filters)]);

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshLoading(true);
        try {
            await fetchUsers();
        } finally {
            setRefreshLoading(false);
        }
    };

    // Handle single delete with better state management
    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        const userId = userToDelete._id || userToDelete.id;
        const result = await dispatch(deleteSingleUser(userId));

        if (result?.success) {
            setShowDeleteModal(false);
            setUserToDelete(null);
            // Only refresh if deletion was successful
            fetchUsers();
        }
    };

    // View user history
    const handleViewHistory = (user) => {
        setSelectedUser(user);
        setShowHistoryModal(true);
    };

    // Handle Password Update
    const handlePasswordUpdate = (user) => {
        setSelectedUser(user);
        setShowPasswordModal(true);
    };

    const handleStatusClick = (user, e) => {
        e?.stopPropagation();
        setSelectedUser(user);
        setShowStatusModal(true);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setShowCreateModal(true);
    };

    const handleMultiplierClick = (user) => {
        setSelectedUser(user);
        setShowMultiplierModal(true);
    };

    // Table columns
    // Define columns based on game type
    const getColumns = () => {
        const baseColumns = [
            {
                name: "Account No.",
                selector: row => row.acnumber || 'N/A',
                sortable: true,
            },
            {
                name: "Role",
                selector: row => row.role || '-',
                sortable: true,
                cell: row => {
                    const role = row.role;
                    if (role === null || role === undefined || role === '') {
                        return <span className="text-muted">-</span>;
                    }
                    return (
                        <span
                            className={`badge bg-${role === 'operator' ? 'warning' : 'info'}`}
                        >
                            {role}
                        </span>
                    );
                }
            },
            {
                name: "Operator",
                selector: row => row.operatorName || 'Admin',
                sortable: true,
            },
            {
                name: 'Online Status',
                selector: row => row.isOnline,
                sortable: true,
                cell: row => {
                    if (row.isOnline) {
                        return (
                            <div className="text-success fw-medium d-flex align-items-center gap-1">
                                <span className="badge bg-success-subtle text-success p-1 rounded-circle" style={{ width: '8px', height: '8px' }}></span>
                                Online
                            </div>
                        );
                    }

                    return (
                        <div className="text-danger fw-medium d-flex align-items-center gap-1">
                            <span className="badge bg-danger p-1 rounded-circle" style={{ width: '8px', height: '8px' }}></span>
                            Offline
                        </div>
                    );
                }
            }
        ];

        // Add game-specific columns only
        if (gameType === 'lt1') {
            baseColumns.push(
                {
                    name: "Total Spent",
                    selector: row => row.lt1TotalSpent || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Total Won",
                    selector: row => row.lt1TotalWon || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Games Played",
                    selector: row => row.lt1GamesPlayed || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Games Won",
                    selector: row => row.lt1GamesWon || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                }
            );
        } else if (gameType === 'ro') {
            baseColumns.push(
                {
                    name: "Total Spent",
                    selector: row => row.roTotalSpent || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Total Won",
                    selector: row => row.roTotalWon || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Games Played",
                    selector: row => row.roGamesPlayed || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Games Won",
                    selector: row => row.roGamesWon || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                }
            );
        } else {
            // Default to LT game
            baseColumns.push(
                {
                    name: "Total Spent",
                    selector: row => row.ltTotalSpent || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Total Won",
                    selector: row => row.ltTotalWon || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Games Played",
                    selector: row => row.ltGamesPlayed || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                },
                {
                    name: "Games Won",
                    selector: row => row.ltGamesWon || 0,
                    sortable: true,
                    omit: true, // Hide from display
                    export: true // Include in export
                }
            );
        }

        // Add remaining columns
        baseColumns.push(
            {
                name: "Coins",
                selector: row => row.coins || 0,
                sortable: true,
                cell: row => (
                    <span
                        className={`badge bg-info`}
                        style={!row.isActive ? { backgroundColor: '#e9ecef', color: '#e9ecef' } : {}}
                    >
                        {row.coins || 0}
                    </span>
                )
            },
            {
                name: "Status",
                cell: row => {
                    // Use isDeleted from API or derived isActive
                    const isActive = row.isActive !== undefined ? row.isActive : !row.isDeleted;
                    const isUserOperator = row.role === 'operator';

                    return (
                        <span
                            className={`badge bg-${isActive ? 'success' : 'danger'}`}
                            onClick={(e) => !isUserOperator && handleStatusClick(row, e)}
                            style={{
                                cursor: isUserOperator ? 'not-allowed' : 'pointer',
                                pointerEvents: isUserOperator ? 'none' : 'auto',
                                opacity: isUserOperator ? 0.6 : 1
                            }}
                            title={isUserOperator ? "Cannot change operator status" : "Click to update status"}
                        >
                            {isActive ? "Active" : "Inactive"}
                        </span>
                    );
                },
                sortable: true,
                sortField: "isActive",
                ignoreRowClick: true
            },
            {
                name: "Win Rate",
                selector: row => row.winMultiplier?.[gameType] ?? 'None',
                sortable: true,
                cell: row => {
                    const isOperator = row.role === 'operator';
                    if (isOperator) return <div className="text-muted text-center w-100">-</div>;

                    const userMultiplier = row.winMultiplier?.[gameType];
                    
                    // Inheritance Logic for Multiplier
                    let effectiveMultiplier = userMultiplier;
                    let multSource = 'user';

                    if (!effectiveMultiplier) {
                        const operatorId = row.createdBy?._id;
                        const operatorData = operators.find(op => op._id === operatorId);
                        const operatorMultiplier = operatorData?.winMultiplier?.[gameType];
                        
                        if (operatorMultiplier) {
                            effectiveMultiplier = operatorMultiplier;
                            multSource = 'operator';
                        } else {
                            const globalVal = getGlobalMultiplier();
                            if (globalVal) {
                                effectiveMultiplier = globalVal;
                                multSource = 'global';
                            }
                        }
                    }

                    return (
                        <div 
                            className="d-flex align-items-center justify-content-center py-1"
                            onClick={() => handleMultiplierClick(row)}
                            style={{ cursor: 'pointer' }}
                        >
                            {/* Multiplier Button */}
                            <div
                                className={`d-inline-flex align-items-center justify-content-center px-3 py-1 fw-semibold fs-11 transition-all`}
                                style={{ 
                                    minWidth: '115px', 
                                    transition: 'all 0.2s ease',
                                    borderRadius: '5px',
                                    backgroundColor: multSource === 'user' ? '#0d9582' : 'transparent',
                                    color: multSource === 'user' ? '#fff' : '#6c757d',
                                    border: multSource === 'user' ? '1px solid #0d9582' : '1px dashed #ced4da'
                                }}
                                title={multSource === 'user' ? "User specific multiplier" : `Inherited from ${multSource}`}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    if (multSource === 'user') e.currentTarget.style.backgroundColor = '#087a6a';
                                    else e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    if (multSource === 'user') e.currentTarget.style.backgroundColor = '#0d9582';
                                    else e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                {multSource === 'global' 
                                    ? 'Rate: Global' 
                                    : (effectiveMultiplier 
                                        ? `Rate: ${effectiveMultiplier}x${multSource === 'operator' ? ' (Op)' : ''}` 
                                        : 'Set Rate')}
                            </div>
                        </div>
                    );
                },
                ignoreRowClick: true
            },
            {
                name: "Created At",
                selector: row => row.createdAt ? formatDateDDMMYYYY(row.createdAt) : 'N/A',
                sortable: true,
                sortField: "createdAt"
            },
            {
                name: "Actions",
                cell: row => {
                    const isRowOperatorAccount = row.role === 'operator';

                    return (
                        <div className="d-flex gap-2">
                            {/* Restore Action - removed as now handled by status modal */}

                            <Button
                                color="primary"
                                size="sm"
                                title={isRowOperatorAccount ? "Cannot edit operator users" : "Edit User"}
                                onClick={() => handleEditUser(row)}
                                disabled={!row.isActive || isCurrentUserOperator || isRowOperatorAccount}
                                style={isRowOperatorAccount ? {
                                    opacity: 0.4,
                                    cursor: 'not-allowed',
                                    filter: 'blur(0.5px)'
                                } : {}}
                            >
                                <i className="ri-pencil-line"></i>
                            </Button>

                            <Button
                                color="warning"
                                size="sm"
                                onClick={() => handlePasswordUpdate(row)}
                                title="Forgot Password"
                                disabled={!row.isActive}
                            >
                                <i className="ri-key-2-line"></i>
                            </Button>

                            <Button
                                color="info"
                                size="sm"
                                onClick={() => handleViewHistory(row)}
                                title="View History"
                                disabled={historyLoading || !row.isActive}
                            >
                                <i className="ri-history-line"></i>
                            </Button>
                            <Button
                                color="danger"
                                size="sm"
                                onClick={() => {
                                    setUserToDelete(row);
                                    setShowDeleteModal(true);
                                }}
                                title={isRowOperatorAccount ? "Cannot delete operator users" : "Delete User"}
                                disabled={!row.isActive || deleteLoading || isRowOperatorAccount}
                                style={isRowOperatorAccount ? {
                                    opacity: 0.4,
                                    cursor: 'not-allowed',
                                    filter: 'blur(0.5px)'
                                } : {}}
                            >
                                <i className="ri-delete-bin-line"></i>
                            </Button>
                        </div>
                    );
                },
                ignoreRowClick: true,
            }
        );

        return baseColumns;
    };

    // Client-side role filtering
    const filteredUsers = roleFilter
        ? users.filter(user => user.role === roleFilter)
        : users;

    const columns = getColumns();

    // Get current date for file name
    const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const gamePrefix = gameType === 'lt1' ? 'lt1' : gameType === 'ro' ? 'ro' : 'lt';
    const exportFileName = `${gamePrefix}-users-${currentDate}`;

    // Debug: Check gameType value
    console.log('=== NEW CODE RUNNING ===');
    console.log('GameType:', gameType, 'FileName:', exportFileName);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-flex align-items-center justify-content-between">
                            <h4 className="mb-0">Users Management</h4>
                        </div>
                    </div>
                </div>

                <CommonDataTable
                    columns={columns}
                    data={filteredUsers}
                    loading={loading}
                    totalRows={roleFilter ? filteredUsers.length : pagination.total}
                    filterParams={filters}
                    fetchData={updateFilterParams}
                    searchEnable={true}
                    showAddButton={true}
                    addOnClickMethod={() => setShowCreateModal(true)}
                    buttonName="Create User"
                    showRefreshButton={true}
                    onRefresh={handleRefresh}
                    refreshLoading={refreshLoading}
                    showExportButton={true}
                    checkboxEnabled={true}
                    bulkSelected={setSelectedRows}
                    selectedRows={selectedRows}
                    selectableRowDisabled={(row) => !row.isActive || row.status === 'inactive' || row.status === 'deleted' || row.userStatus === 'deleted'}
                    exportFileName={exportFileName}
                    customFilters={
                        <div className="d-flex gap-2">
                            <select
                                className="form-select"
                                style={{ width: "150px" }}
                                value={filters.status || ""}
                                onChange={(e) => {
                                    updateFilterParams({
                                        page: 1,
                                        status: e.target.value || undefined
                                    });
                                }}
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>

                            <select
                                className="form-select"
                                style={{ width: "130px" }}
                                value={roleFilter}
                                onChange={(e) => {
                                    setRoleFilter(e.target.value);
                                }}
                            >
                                <option value="">All Roles</option>
                                <option value="user">User</option>
                                <option value="operator">Operator</option>
                            </select>

                            <select
                                className="form-select"
                                style={{ width: "180px" }}
                                value={filters.operatorId || ""}
                                onChange={(e) => {
                                    updateFilterParams({
                                        page: 1,
                                        operatorId: e.target.value || undefined
                                    });
                                }}
                            >
                                <option value="">All Operators</option>
                                {loadingOperators ? (
                                    <option disabled>Loading...</option>
                                ) : (
                                    operators.map((op) => (
                                        <option key={op._id} value={op._id}>
                                            {op.username}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    }
                    conditionalRowStyles={[
                        {
                            when: row => row.isOnline,
                            classNames: ['online-blink-row']
                        },
                        {
                            when: (row) => !row.isActive || row.status === 'inactive',
                            style: {
                                // opacity: 0.5, // Removed to keep badge vivid
                                backgroundColor: '#f0f0f0',
                                color: '#b0b0b0', // Fade text instead
                                pointerEvents: 'none',
                            }
                        }
                    ]}
                />

                {/* History Modal */}
                <UserHistoryModal
                    isOpen={showHistoryModal}
                    toggle={() => {
                        setShowHistoryModal(false);
                        setSelectedUser(null);
                    }}
                    user={selectedUser}
                    gameType={gameType}
                />

                {/* Password Update Modal */}
                <PasswordUpdateModal
                    isOpen={showPasswordModal}
                    toggle={() => {
                        setShowPasswordModal(false);
                        setSelectedUser(null);
                    }}
                    user={selectedUser}
                />

                {/* Status Update Modal */}
                <UserStatusUpdateModal
                    isOpen={showStatusModal}
                    toggle={() => {
                        setShowStatusModal(false);
                        setSelectedUser(null);
                    }}
                    user={selectedUser}
                />

                {/* Create User Modal */}
                <CreateUserModal
                    show={showCreateModal}
                    toggle={() => {
                        setShowCreateModal(!showCreateModal);
                        if (showCreateModal) setSelectedUser(null);
                    }}
                    userToEdit={selectedUser}
                />

                {/* Win Multiplier Modal */}
                <WinMultiplierModal
                    isOpen={showMultiplierModal}
                    toggle={() => {
                        setShowMultiplierModal(false);
                        setSelectedUser(null);
                    }}
                    target={selectedUser}
                    gameType={gameType}
                />

                {/* Delete Confirmation Modal */}
                <DeleteDialog
                    isOpen={showDeleteModal}
                    toggle={() => {
                        setShowDeleteModal(false);
                        setUserToDelete(null);
                    }}
                    onConfirm={handleDeleteUser}
                    title="Delete User"
                    message={`Are you sure you want to delete user with account number ${userToDelete?.acnumber || ''}? This action cannot be undone.`}
                    confirmLoading={deleteLoading}
                />
            </div>
        </div>
    );
};

export default Users;